import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import './UserStatusBadge.css';

const UserStatusBadge = ({ user, memberInfo, onSignOut }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user?.id]);

  useEffect(() => {
    // Click outside to close
    const handleClickOutside = (event) => {
      if (badgeRef.current && !badgeRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  const loadProfileData = async () => {
    try {
      const { data, error } = await supabase
        .from('verified_users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (data) {
        setProfileData(data);

        // Calculate days remaining for provisional status
        if (data.provisional_until) {
          const daysLeft = Math.ceil(
            (new Date(data.provisional_until) - new Date()) / (1000 * 60 * 60 * 24)
          );
          setDaysRemaining(Math.max(0, daysLeft));
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  if (!user) return null;

  const getStatusColor = () => {
    if (!profileData) return 'incomplete';

    // Check approval_status first - this determines if admin has approved the user
    if (profileData.approval_status === 'admin_approved' ||
        profileData.approval_status === 'auto_approved' ||
        profileData.approval_status === 'call_verified') {
      return 'approved';  // GREEN BADGE - Admin approved
    }

    // Check verification_status for provisional/email-verified users
    if (profileData.verification_status === 'email_verified' ||
        profileData.verification_status === 'provisional') {
      return 'provisional';  // YELLOW/ORANGE - Pending admin approval
    }

    // Legacy verification_status values
    if (profileData.verification_status === 'approved' ||
        profileData.verification_status === 'verified') {
      return 'approved';
    }

    if (profileData.verification_status === 'restricted' ||
        profileData.verification_status === 'blocked') {
      return 'restricted';
    }

    return 'incomplete';
  };

  const getStatusText = () => {
    if (!profileData) return 'Profile Incomplete';

    // Admin approved = fully verified (GREEN BADGE)
    if (profileData.approval_status === 'admin_approved' ||
        profileData.approval_status === 'auto_approved' ||
        profileData.approval_status === 'call_verified') {
      return 'Verified Member';
    }

    // Email verified but not admin approved = provisional (YELLOW BADGE)
    if (profileData.verification_status === 'email_verified') {
      return 'Provisional - Pending Approval';
    }

    // Provisional period with countdown
    if (profileData.verification_status === 'provisional') {
      return `Provisional (${daysRemaining} days left)`;
    }

    // Legacy or other statuses
    if (profileData.verification_status === 'approved') {
      return 'Verified Member';
    }

    if (profileData.verification_status === 'restricted') {
      return 'Restricted Access';
    }

    if (profileData.verification_status === 'blocked') {
      return 'Account Blocked';
    }

    return 'Pending Verification';
  };

  const formatPhone = (phone) => {
    if (!phone) return 'No phone';
    // Format from raw phone_number — don't use display_format (may be stale)
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === '1') {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const getInitials = () => {
    if (profileData?.first_name && profileData?.last_name) {
      return `${profileData.first_name[0]}${profileData.last_name[0]}`.toUpperCase();
    }
    return user.email?.[0]?.toUpperCase() || '?';
  };

  const getDisplayName = () => {
    if (profileData?.first_name && profileData?.last_name) {
      return `${profileData.first_name} ${profileData.last_name[0]}.`;
    }
    return user.email?.split('@')[0] || 'User';
  };

  const handleSignOut = async () => {
    setIsExpanded(false);
    if (onSignOut) {
      onSignOut();
    } else {
      await supabase.auth.signOut();
      window.location.reload();
    }
  };

  const statusClass = getStatusColor();

  return (
    <div className={`user-status-badge ${isExpanded ? 'expanded' : ''}`} ref={badgeRef}>
      {/* Collapsed View */}
      <div
        className="badge-collapsed"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="user-avatar">
          <span className="avatar-initials">{getInitials()}</span>
        </div>
        <div className="user-quick-info">
          <div className="user-name">{getDisplayName()}</div>
          <div className={`user-status status-${statusClass}`}>
            <span className="status-dot"></span>
            {!isExpanded && getStatusText()}
          </div>
        </div>
        <button className="expand-toggle">
          {isExpanded ? '▼' : '▲'}
        </button>
      </div>

      {/* Expanded View */}
      {isExpanded && (
        <div className="badge-expanded">
          {/* User Information Section */}
          <div className="expanded-section user-details">
            <h4>Account Details</h4>
            <div className="detail-row">
              <span className="detail-icon">📧</span>
              <span className="detail-text">{user.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-icon">📱</span>
              <span className="detail-text">{formatPhone(profileData?.phone_number)}</span>
            </div>
            {profileData?.city && profileData?.state && (
              <div className="detail-row">
                <span className="detail-icon">📍</span>
                <span className="detail-text">{profileData.city}, {profileData.state}</span>
              </div>
            )}
          </div>

          {/* Status Information Section */}
          <div className="expanded-section status-details">
            <h4>Verification Status</h4>
            <div className={`status-badge status-${statusClass}`}>
              <span className="status-dot large"></span>
              <span className="status-label">{getStatusText()}</span>
            </div>

            {profileData?.verification_status === 'provisional' && (
              <div className="status-timeline">
                <div className="timeline-progress">
                  <div
                    className="timeline-fill"
                    style={{ width: `${((7 - daysRemaining) / 7) * 100}%` }}
                  ></div>
                </div>
                <div className="timeline-text">
                  {daysRemaining > 0
                    ? `${daysRemaining} days remaining to complete verification`
                    : 'Provisional period expired'
                  }
                </div>
              </div>
            )}

            {profileData?.recognition_status && (
              <div className="recognition-status">
                <span className="detail-label">Recognition:</span>
                <span className="detail-value">
                  {profileData.recognition_status === 'recognized' ? '✓ Recognized' :
                   profileData.recognition_status === 'pending' ? '⏳ Pending Review' :
                   '○ Not submitted'}
                </span>
              </div>
            )}

            {profileData?.email_verified !== undefined && (
              <div className="email-status">
                <span className="detail-label">Email:</span>
                <span className="detail-value">
                  {profileData.email_verified ? '✓ Verified' : '⚠️ Unverified'}
                </span>
              </div>
            )}
          </div>

          {/* Quick Actions Section */}
          <div className="expanded-section quick-actions">
            <button className="action-button secondary">
              <span>👤</span> View Profile
            </button>
            <button className="action-button secondary">
              <span>✏️</span> Edit Profile
            </button>
            <button className="action-button danger" onClick={handleSignOut}>
              <span>🚪</span> Sign Out
            </button>
          </div>

          {/* Notifications/Warnings */}
          {daysRemaining && daysRemaining <= 2 && daysRemaining > 0 && (
            <div className="warning-message">
              ⚠️ Your provisional access expires in {daysRemaining} days. Please complete verification.
            </div>
          )}

          {profileData && !profileData.email_verified && (
            <div className="warning-message">
              📧 Please verify your email to unlock all features.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserStatusBadge;